// packages/types/src/theme.ts
//
// Framework-free theme core. Lives in @alamo/types (a package both @alamo/auth
// and @alamo/ui depend on) so the AuthProvider can apply the server-persisted
// theme without importing the React ThemeContext — keeping auth decoupled from
// the UI data-layer while still driving the single `.dark` class + localStorage.

/**
 * Theme preference persisted in `users.preferences.theme.mode`.
 * `auto` follows the device `prefers-color-scheme`, resolved on the frontend.
 */
export type ThemeMode = 'light' | 'dark' | 'auto';

/** Runtime companion of {@link ThemeMode} (for selectors / validation). */
export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'auto'] as const;

/** Concrete theme after resolving `auto` against the device preference. */
export type ResolvedTheme = 'light' | 'dark';

/** localStorage key mirroring the current theme mode for pre-login application. */
export const THEME_STORAGE_KEY = 'alamo_theme';

/**
 * Window event dispatched whenever the theme mode is applied outside the React
 * ThemeProvider (e.g. AuthProvider applying the server preference on login).
 * The ThemeProvider listens to it to keep its state in sync.
 */
export const THEME_APPLIED_EVENT = 'alamo:theme-applied';

/** Detail payload of {@link THEME_APPLIED_EVENT}. */
export type ThemeAppliedEventDetail = { mode: ThemeMode };

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'auto';
}

/**
 * Public, anonymous, single-use journeys the associado/cliente reaches WITHOUT a
 * login — no account/preference/toggle. The client requires these to ALWAYS
 * render light, regardless of the device or any stored theme:
 *   - `/p/...`      MagicLink proposal journey
 *   - `/quote...`   public quotation landing (`/quote`, `/quote/:slug`)
 *   - `/cotacao...` legacy PT redirects to `/quote`
 *   - `/q/...`      public read-only quotation view (QR-B)
 * NOTE: the LOGGED-IN consultor's quotation screens live under different paths
 * (`/quotes`, `/quotations/:id`, `/proposals/:id`) and are NOT forced-light.
 */
const FORCED_LIGHT_PATH_PREFIXES = ['/p/', '/quote', '/cotacao', '/q/'] as const;

/**
 * True when the given pathname belongs to a public flow that must always render
 * light (no `.dark`, no toggle). Shared source of truth for the anti-flash inline
 * script (mirrored by hand in index.html), the ThemeProvider, and the runtime
 * lock — so all three agree on which routes are forced-light.
 *
 * A prefix ending in `/` also matches its bare form (`/p/` matches `/p`); a
 * prefix without a trailing slash (`/quote`) matches `/quote`, `/quote/…` but not
 * an unrelated sibling like `/quotes` (guarded by requiring the next char to be
 * `/` or end-of-string).
 */
export function isForcedLightPath(pathname: string): boolean {
  return FORCED_LIGHT_PATH_PREFIXES.some((prefix) => {
    if (prefix.endsWith('/')) {
      return pathname === prefix.slice(0, -1) || pathname.startsWith(prefix);
    }
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

// Module-level lock. While active, `applyResolvedThemeClass` refuses to add the
// `.dark` class, so any actor (ThemeProvider, AuthProvider server theme, other
// tab) that resolves to dark still leaves the page light — WITHOUT mutating the
// persisted `mode` in localStorage. Toggling the lock reapplies the current mode.
let forcedLightLock = false;

/** Whether the forced-light lock is currently engaged. */
export function isForcedLightLocked(): boolean {
  return forcedLightLock;
}

/** True when the device currently prefers a dark color scheme. */
export function prefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Resolve a mode to a concrete light/dark value (`auto` → device preference). */
export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'auto') {
    return prefersDark() ? 'dark' : 'light';
  }
  return mode;
}

/** Read the persisted mode from localStorage; falls back to `auto`. */
export function readStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'auto';
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

/** Add or remove the `.dark` class on `<html>` for the resolved theme. */
export function applyResolvedThemeClass(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // The lock wins over any dark resolution: forced-light routes stay light.
  if (resolved === 'dark' && !forcedLightLock) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Engage or release the forced-light lock and re-apply `mode` so the `<html>`
 * class reflects the new state immediately. Called by the router-level lock when
 * entering/leaving a forced-light route. Does NOT persist or change `mode` — it
 * only gates whether `.dark` may be applied, so leaving the route restores the
 * user's real preference untouched.
 */
export function setForcedLightLock(active: boolean, mode: ThemeMode): void {
  forcedLightLock = active;
  applyResolvedThemeClass(resolveTheme(mode));
}

/**
 * Persist the mode, apply the resolved `.dark` class, and notify listeners.
 * Framework-free entry point shared by AuthProvider (server theme on login) and
 * the React ThemeProvider.
 */
export function applyThemeMode(mode: ThemeMode): ResolvedTheme {
  const resolved = resolveTheme(mode);
  applyResolvedThemeClass(resolved);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // Private mode / storage disabled — class is still applied for this tab.
    }
    window.dispatchEvent(
      new CustomEvent<ThemeAppliedEventDetail>(THEME_APPLIED_EVENT, { detail: { mode } }),
    );
  }
  return resolved;
}
